import { useLazyQuery, useMutation } from '@apollo/client'
import ButtonBase from 'components/partials/button'
import DropdownBase, { Option } from 'components/partials/dropDown'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import { findDuplicates } from 'helpers/functionUtils'
import {
  ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG,
  AgencyGroupJobTypeEnum,
  IAgencyGroupJobConfigInput,
} from 'queries/admin/agencyGroup'
import { GET_ALL_EMAIL_MANAGEMENT_CONFIG } from 'queries/admin/emailManagementConfig'
import React, { useEffect, useState } from 'react'
import {
  IAgencyGroup,
  IAgencyGroupJobConfig,
  IEmailManagementConfig,
  ISubEmailManagementConfig,
} from '../../interface'
import CustomTemplateForStage from '../components/customTemplate'
import { AGENCY_GROUP } from 'constants/common'
import { useSession } from 'helpers/checkSession'

interface Props {
  agencyGroup: IAgencyGroup
  agencyGroupJobConfigs: IAgencyGroupJobConfig[]
  handleChangeEditMode: (isEdit: boolean) => void
  refetchDetailData: () => void
}

interface IDropdownEmailConfigOption extends Option {
  totalSub: number
  subEmailConfigs: ISubEmailManagementConfig[]
}

const EditTemplateCustomization: React.FC<Props> = ({
  agencyGroup,
  agencyGroupJobConfigs,
  handleChangeEditMode,
  refetchDetailData,
}) => {
  const { handleErrorSession } = useSession()
  const [currentAgencyGroup, setCurrentAgencyGroup] = useState<IAgencyGroup>(agencyGroup)
  const [currentEmailConfig, setCurrentEmailConfig] = useState<IEmailManagementConfig | null>()
  const [customConfigs, setCustomConfigs] = useState<IAgencyGroupJobConfig[]>(agencyGroupJobConfigs)
  const [maxCustomConfig, setMaxCustomConfig] = useState<number>(0)
  const [isDisabledAddMoreBtn, setIsDisabledAddMoreBtn] = useState<boolean>(false)
  const [dropdownEmailConfigOptions, setDropdownEmailConfigOptions] = useState<
    IDropdownEmailConfigOption[]
  >([])
  const [selectedEmailConfigOption, setSelectedEmailConfigOption] =
    useState<IDropdownEmailConfigOption>()

  const [getAllEmailConfigs] = useLazyQuery(GET_ALL_EMAIL_MANAGEMENT_CONFIG, {
    variables: { limit: 50, page: 1 },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data) return

      const { items } = data.getAllEmailManagementConfig

      const emailConfigOptions: IDropdownEmailConfigOption[] = []

      for (const item of items) {
        const option: IDropdownEmailConfigOption = {
          id: item.emailManagementConfigId,
          name: `${item.startSubject} - ${item.cancelSubject}`,
          subEmailConfigs: item.subEmailManagementConfig ?? [],
          totalSub: item.subEmailManagementConfig?.length ?? 0,
        }

        emailConfigOptions.push(option)
      }

      const temp = emailConfigOptions.find(
        (item) => item.id === currentEmailConfig?.emailManagementConfigId,
      )
      if (temp) {
        setMaxCustomConfig(temp.totalSub)
      }
      setDropdownEmailConfigOptions(emailConfigOptions)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const [addOrEditAgencyGroupJobConfig] = useMutation(ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG)

  const validateData = () => {
    let isValid = true
    const errorMessages: string[] = []

    const { hasDuplicate, duplicateData } = findDuplicates(
      customConfigs,
      'subEmailManagementConfigId',
    )

    if (hasDuplicate) {
      const tDayConfigDuplicates = duplicateData.map(
        (item) => item.subEmailManagementConfig?.tDayConfig.key,
      )
      errorMessages.push(`Duplicate tDay config: ${tDayConfigDuplicates.join(', ').trim()}`)

      isValid = false
      return {
        isValid,
        errorMessages,
      }
    }

    for (const custom of customConfigs) {
      if (!custom.customEmailType || !custom.subEmailManagementConfigId) {
        isValid = false
        errorMessages.push(`Missing type or tDay`)
        break
      }

      if (
        custom.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.DOCUMENT_URL &&
        (custom.emailTemplateURL === '' || !custom.emailTemplateURL)
      ) {
        isValid = false
        errorMessages.push(`Missing document URL`)
        break
      }

      if (
        custom.customEmailType === AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.HTML_CSS &&
        (!custom.htmlTemplateString || custom.htmlTemplateString === '')
      ) {
        isValid = false
        errorMessages.push(`Missing html template string`)
        break
      }
    }

    return { isValid, errorMessages }
  }

  const onClickSaveBtn = () => {
    const { errorMessages, isValid } = validateData()
    if (!isValid) {
      // notify('Invalid data input. Please try again!', STATUS.ERROR)
      errorMessages.forEach((error) => {
        notify(error, STATUS.ERROR)
      })
      return
    } else {
      const customConfigInputs: IAgencyGroupJobConfigInput[] = []
      for (const item of customConfigs) {
        const data: IAgencyGroupJobConfigInput = {
          customJobType: AgencyGroupJobTypeEnum.CUSTOM_EMAIL,
          emailTemplateURL: item.emailTemplateURL,
          htmlTemplateString: item.htmlTemplateString,
          subEmailManagementConfigId: item.subEmailManagementConfigId as string,
          customEmailType: item.customEmailType as string,
          forwardWebhook: null,
          forwardEmail: null,
          forwardFormatTemplate: null,
        }
        customConfigInputs.push(data)
      }

      addOrEditAgencyGroupJobConfig({
        variables: {
          data: {
            agencyGroupId: currentAgencyGroup.agencyGroupId,
            customJobType: AgencyGroupJobTypeEnum.CUSTOM_EMAIL,
            customConfigs: customConfigInputs,
          },
        },
        onCompleted: () => {
          handleChangeEditMode(false)
          refetchDetailData()
          notify('Email configuration updated successfully!', STATUS.SUCCESS)
        },
        onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
      })
    }
  }

  const onCancelBtn = () => {
    handleChangeEditMode(false)
  }

  const onClickAddMoreBtn = () => {
    let tempIndex = 0
    if (customConfigs.length > 0) {
      tempIndex = customConfigs[customConfigs.length - 1].index as number
    }
    if (customConfigs.length < maxCustomConfig) {
      const newCustomEmailTemplate: IAgencyGroupJobConfig = {
        index: tempIndex + 1,
        agencyGroupId: currentAgencyGroup.agencyGroupId,
        agencyGroup: null,
        emailTemplateURL: '',
        customEmailType: null,
        subEmailManagementConfig: null,
        subEmailManagementConfigId: null,
        htmlTemplateString: null,
        agencyGroupJobConfigId: null,
        customJobType: 'CUSTOM_EMAIL',
        forwardWebhook: null,
        forwardEmail: {},
        forwardFormatTemplate: null,
      }
      setCustomConfigs([...customConfigs, newCustomEmailTemplate])
    }
  }

  const handleRemoveOneCustom = (index: number | null) => {
    if (index === null) return
    setCustomConfigs((prev) => prev.filter((item) => item.index !== index))
  }

  const onSelectEmailConfig = (option: IDropdownEmailConfigOption) => {
    if (option.id !== selectedEmailConfigOption?.id) {
      setCustomConfigs([])
    }
    setMaxCustomConfig(option.totalSub)
    setSelectedEmailConfigOption(option)
  }

  const setCustomConfigByIndex = (config: IAgencyGroupJobConfig) => {
    const tempIndex = customConfigs.findIndex((item) => item.index === config.index)
    if (tempIndex === -1) return

    const customConfigTemp = [...customConfigs]

    customConfigTemp[tempIndex] = config

    setCustomConfigs(customConfigTemp)
  }

  useEffect(() => {
    if (agencyGroup) {
      setCurrentAgencyGroup(agencyGroup)
    }
  }, [agencyGroup])

  useEffect(() => {
    if (agencyGroupJobConfigs && agencyGroupJobConfigs.length > 0) {
      const emailConfig = agencyGroupJobConfigs[0].subEmailManagementConfig?.emailManagementConfig
      if (emailConfig) {
        setCurrentEmailConfig(emailConfig)
      }
      setCustomConfigs(agencyGroupJobConfigs.map((item, index) => ({ ...item, index })))
    } else {
      //  New
      setCurrentEmailConfig(null)
    }
  }, [agencyGroupJobConfigs])

  useEffect(() => {
    getAllEmailConfigs()
  }, [])

  useEffect(() => {
    if (!currentEmailConfig || dropdownEmailConfigOptions.length === 0) return

    const option = dropdownEmailConfigOptions.find(
      (item) => item.id === currentEmailConfig.emailManagementConfigId,
    )
    if (option) {
      setSelectedEmailConfigOption(option)
    }
  }, [currentEmailConfig, dropdownEmailConfigOptions])

  useEffect(() => {
    setIsDisabledAddMoreBtn(customConfigs.length >= maxCustomConfig)
  }, [maxCustomConfig, customConfigs.length])

  useEffect(() => {
    setCurrentAgencyGroup({
      ...currentAgencyGroup,
      agencyGroupJobConfigs: customConfigs,
    })
  }, [customConfigs])

  if (!currentAgencyGroup) return null

  return (
    <div className="space-y-2">
      <div className="flex justify-end space-x-2">
        <ButtonBase onClick={onClickSaveBtn}>Save</ButtonBase>
        <ButtonBase onClick={onCancelBtn}>Cancel</ButtonBase>
      </div>

      <div className="w-full">
        {currentEmailConfig ? (
          <DropdownBase
            className="w-full"
            options={dropdownEmailConfigOptions}
            placeHolder="Select the start & cancel subject"
            handleOptions={(option) => onSelectEmailConfig(option as IDropdownEmailConfigOption)}
            initialValue={`${currentEmailConfig.startSubject} - ${currentEmailConfig.cancelSubject}`}
          />
        ) : (
          <DropdownBase
            className="w-full"
            options={dropdownEmailConfigOptions}
            placeHolder="Select the start & cancel subject"
            handleOptions={(option) => onSelectEmailConfig(option as IDropdownEmailConfigOption)}
          />
        )}
      </div>
      <span className="text-sm italic">Total: {maxCustomConfig} stages</span>

      <div className="flex justify-between">
        <ButtonBase
          className="w-[150px]"
          onClick={onClickAddMoreBtn}
          disabled={isDisabledAddMoreBtn}
          bgDisabled="bg-primary-shade3"
        >
          Add More
        </ButtonBase>
      </div>

      {customConfigs && selectedEmailConfigOption && (
        <div className="w-full space-y-2">
          {customConfigs.map((item) => {
            const index = item.index
            return (
              <CustomTemplateForStage
                key={`custom-email-template-${index ?? 0}`}
                config={item}
                isEditMode={true}
                subEmailConfigs={selectedEmailConfigOption.subEmailConfigs}
                setCustomConfigByIndex={setCustomConfigByIndex}
                handleRemoveOneCustom={handleRemoveOneCustom}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default EditTemplateCustomization
